/**
 * Form Applicability Logic
 *
 * Determines which KYC forms are required based on the identification form data
 */

import type { IdentificationFormData } from '@/components/forms/kyc/identification';
import type { FormKData } from '@/components/forms/kyc/types/formK.types';

/**
 * Legal forms that require Form K when country is Switzerland
 * General Partnership (Kollektivgesellschaft), Limited Partnership
 * (Kommanditgesellschaft), Stock Corporation (AG), Limited Liability Company
 * (GmbH), Partnership Limited by Shares, Cooperative, Association, and Other.
 * Note: Foundation (Stiftung) does not trigger Form K for Switzerland.
 */
const FORM_K_APPLICABLE_LEGAL_FORMS: ReadonlySet<string> = new Set([
  'kollektivgesellschaft',
  'kommanditgesellschaft',
  'ag',
  'gmbh',
  'kommanditaktiengesellschaft',
  'genossenschaft',
  'verein',
  'other',
]);

/**
 * Determines if Form K is required based on identification form data
 *
 * Form K applicability: Required if:
 *
 * (Country is Switzerland
 *    AND Legal form is one of the applicable Swiss legal forms)
 * OR Country is different from Switzerland
 *
 * AND NOT (
 *   (Financial intermediary = Yes AND type is "bank" OR "vorsorge_einrichtung")
 *   OR Listed company = Yes
 *   OR Part of listed company = Yes
 * )
 *
 * @param identificationData - Data from the identification form
 * @returns boolean - true if Form K is required, false otherwise
 */
export function isFormKRequired(
  identificationData?: IdentificationFormData
): boolean {
  if (!identificationData) {
    return false;
  }

  const {
    legalForm,
    isFinancialIntermediary,
    listedCompany,
    subsidiaryOwnership,
    country,
    financialIntermediaryType,
  } = identificationData;

  // Country must be specified to determine Form K applicability
  // Without a country, we cannot determine if Form K is required
  if (!country) {
    return false;
  }

  // Exclusion: financial intermediary is checked (yes) AND type is bank or pension institution
  const isExcludedByFinancialIntermediary =
    isFinancialIntermediary === 'yes' &&
    (financialIntermediaryType === 'bank' ||
      financialIntermediaryType === 'vorsorge_einrichtung');

  if (isExcludedByFinancialIntermediary) {
    return false;
  }

  // Check if country is Switzerland (using ISO 3166-1 alpha-2 code)
  const isSwitzerland = country.toUpperCase() === 'CH';

  // Condition 1: Country is Switzerland AND legal form is applicable
  if (isSwitzerland) {
    const hasApplicableLegalForm =
      legalForm !== undefined && FORM_K_APPLICABLE_LEGAL_FORMS.has(legalForm);

    if (!hasApplicableLegalForm) {
      return false;
    }
  }
  // Condition 2: Country is different from Switzerland (no legal form check needed)

  // Exclusion: listed company or part of a listed company
  const isListedCompanyYes = listedCompany === 'yes';
  const isSubsidiaryYes = subsidiaryOwnership === 'yes';

  return !(isListedCompanyYes || isSubsidiaryYes);
}

/**
 * Determines if Form A is required based on identification form data and Form K data
 *
 * Form A applicability: Required if:
 * - Legal form ∈ {Einzelunternehmen}
 * - OR Country is Switzerland AND Legal form = Einfache Gesellschaft (Simple Partnership)
 * - OR 'Fiduciary holding of assets / Origin of assets' = Yes
 *
 * @param identificationData - Data from the identification form
 * @param formKData - Data from Form K (optional, used for dynamic applicability)
 * @returns boolean - true if Form A is required, false otherwise
 */
export function isFormARequired(
  identificationData?: IdentificationFormData,
  formKData?: FormKData
): boolean {
  if (!identificationData) {
    return false;
  }

  const { legalForm, country } = identificationData;

  // Form A is required for Einzelunternehmen (sole proprietorship)
  if (legalForm === 'einzelunternehmen') {
    return true;
  }

  // Form A is required for Simple Partnership (Einfache Gesellschaft) in Switzerland
  const isSwitzerland = country?.toUpperCase() === 'CH';
  if (legalForm === 'einfache_gesellschaft' && isSwitzerland) {
    return true;
  }

  // Form A is required if 'Fiduciary holding of assets / Origin of assets' = Yes (from Form K)
  if (formKData?.fiduciaryHolding === 'yes') {
    return true;
  }

  return false;
}

/**
 * Gets the list of applicable tabs/forms based on identification data and Form K data
 *
 * @param identificationData - Data from the identification form
 * @param formKData - Data from Form K (optional, used for dynamic applicability)
 * @returns Array of form IDs that should be shown
 */
export function getApplicableForms(
  identificationData?: IdentificationFormData,
  formKData?: FormKData
): string[] {
  const baseForms = ['identification'];

  if (!identificationData) {
    return baseForms;
  }

  const applicableForms = [...baseForms];

  // Add Form K if required
  if (isFormKRequired(identificationData)) {
    applicableForms.push('form-k');
  }

  // Add Form A if required based on new logic
  if (isFormARequired(identificationData, formKData)) {
    applicableForms.push('form-a');
  }

  return applicableForms;
}

/**
 * Gets the next applicable form after completing a form
 *
 * @param currentFormId - The form that was just completed
 * @param identificationData - Data from the identification form
 * @param formKData - Data from Form K (optional, used for dynamic applicability)
 * @returns The next form ID or null if this is the last form
 */
export function getNextApplicableForm(
  currentFormId: string,
  identificationData?: IdentificationFormData,
  formKData?: FormKData
): string | null {
  const applicableForms = getApplicableForms(identificationData, formKData);
  const currentIndex = applicableForms.indexOf(currentFormId);

  if (currentIndex === -1 || currentIndex === applicableForms.length - 1) {
    return null;
  }

  return applicableForms[currentIndex + 1];
}

/**
 * Checks if a specific form should be shown
 *
 * @param formId - The form to check
 * @param identificationData - Data from the identification form
 * @param formKData - Data from Form K (optional, used for dynamic applicability)
 * @returns boolean - true if the form should be shown
 */
export function shouldShowForm(
  formId: string,
  identificationData?: IdentificationFormData,
  formKData?: FormKData
): boolean {
  const applicableForms = getApplicableForms(identificationData, formKData);
  return applicableForms.includes(formId);
}
